import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ChatPage.css';
import './VoiceRecording.css';
import ThinkingMarkdownRenderer from './ThinkingMarkdownRenderer';
import SafeAreaWrapper from './SafeAreaWrapper';
import { aiChatService, ChatMessage, SessionManager } from '../services/aiChatService';
import { Customer, customerService } from '../services/customerService';
import { voiceRecordingService, TranscriptionResult } from '../services/voiceRecordingService';
// 移除钉钉认证依赖
import { imageUploadService } from '../services/imageUploadService';
import { fileUploadService, FileUploadResponseItem } from '../services/fileUploadService';
import { messageService, FileUploadResponse, ChatMessageRecord } from '../services/messageService';
import { getLastSession, SessionMessage } from '../services/sessionService';
import {
    QuickActionsDrawer,
    PermissionModal,
    VoiceRecordingModal,
    PermissionType
} from './features';
import { useAuth } from '../contexts/AuthContext';
import { getWeChatOpenIdFromUrl } from '../services/authService';

// 前端消息接口，简化处理
interface Message {
    id: number;
    content: string;
    sender: 'user' | 'client'; // 简化的发送者类型
    timestamp: string; // 格式化后的时间字符串
    isStreaming?: boolean; // 流式响应状态
    metadata?: any; // 后端的 metadata，用于传递给 ThinkingMarkdownRenderer
}

// 简单的toast替代函数
const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    console.log(`Toast [${type}]: ${message}`);
    // 在实际应用中，可以使用更好的通知库
};

const ChatPage: React.FC = () => {
    const { clientId } = useParams<{ clientId: string }>();
    const navigate = useNavigate();
    const [newMessage, setNewMessage] = useState('');
    const [showQuickActions, setShowQuickActions] = useState(false);
    const [activeTab, setActiveTab] = useState<'chat' | 'functions'>('chat');

    const [isLoading, setIsLoading] = useState(false);

    // 认证上下文
    const { logout, userInfo, weChatOpenId } = useAuth();

    // 处理注销
    const handleLogout = async () => {
        try {
            // 获取weChatOpenId用于注销后跳转
            const openId = weChatOpenId || getWeChatOpenIdFromUrl();
            console.log('注销时的weChatOpenId:', openId);
            
            // 执行注销
            await logout();
            
            // 跳转到登录页面，如果有weChatOpenId则保留
            if (openId) {
                console.log('注销后跳转到登录页面，携带weChatOpenId:', openId);
                navigate(`/login?weChatOpenId=${openId}`);
            } else {
                console.log('注销后跳转到登录页面，无weChatOpenId');
                navigate('/login');
            }
        } catch (error) {
            console.error('注销失败:', error);
            showToast('注销失败', 'error');
        }
    };
    // 使用当前登录用户信息
    const getUserDisplayInfo = () => {
        if (userInfo) {
            return {
                KUNNR: userInfo.userId || userInfo.login || 'default',
                NAME1: userInfo.name || userInfo.login || '用户'
            };
        }
        // 如果没有用户信息，使用默认值
        return {
            KUNNR: clientId || 'default',
            NAME1: '演示客户'
        };
    };

    const currentUser = getUserDisplayInfo();
    // 移除客户统计信息
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const attachmentInputRef = useRef<HTMLInputElement>(null);

    // 语音录制相关状态
    const [isVoiceRecording, setIsVoiceRecording] = useState(false);
    const [voiceConnectionStatus, setVoiceConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'recording' | 'stopped' | 'error'>('disconnected');
    const [transcriptionText, setTranscriptionText] = useState('');
    const [showVoiceModal, setShowVoiceModal] = useState(false);
    const [hasProcessedFinalResult, setHasProcessedFinalResult] = useState(false);

    // 拍照相关状态
    const [isCameraLoading, setIsCameraLoading] = useState(false);

    // 通用权限模态框状态
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [permissionType, setPermissionType] = useState<PermissionType>('microphone');
    const [permissionError, setPermissionError] = useState('');
    // 累计当前录音的识别文本
    const currentRecordingTranscriptRef = useRef<string>('');
    // 语音录制的累计转录文本（不显示在输入框中）
    const [voiceTranscriptText, setVoiceTranscriptText] = useState('');
    // 播放中的音频
    const [playingAudioId, setPlayingAudioId] = useState<number | null>(null);
    const audioRefMap = useRef<Map<number, HTMLAudioElement>>(new Map());
    // 录音计时（秒）
    const [recordingElapsedSec, setRecordingElapsedSec] = useState(0);
    
    // 头像下拉菜单状态
    const [showAvatarDropdown, setShowAvatarDropdown] = useState(false);
    const avatarDropdownRef = useRef<HTMLDivElement>(null);

    // 恢复会话相关状态
    const [showEntryChoiceModal, setShowEntryChoiceModal] = useState(true);
    const [hasPendingRestorePrompt, setHasPendingRestorePrompt] = useState(false);
    const [showRestoreSessionModal, setShowRestoreSessionModal] = useState(false);
    const [lastSessionData, setLastSessionData] = useState<SessionMessage[]>([]);
    const [lastSessionId, setLastSessionId] = useState<string>('');
    const [hasCheckedLastSession, setHasCheckedLastSession] = useState(false);

    // 移除客户统计信息折叠状态

    // Safari 检测
    const isSafari = typeof navigator !== 'undefined' && /safari/i.test(navigator.userAgent) && !/chrome|crios|android/i.test(navigator.userAgent);

    // 将后端ChatMessageRecord转换为前端Message格式
    const convertChatMessageRecordToMessage = (record: ChatMessageRecord): Message => {
        return {
            id: record.id,
            content: record.content,
            sender: record.fromUser ? 'user' : 'client',
            timestamp: new Date(record.createdAt).toLocaleTimeString('zh-CN', {
                hour: '2-digit',
                minute: '2-digit'
            }),
            metadata: record.metadata,
        };
    };



    // 移除客户信息加载逻辑，使用默认数据

    // 移除客户统计信息自动折叠逻辑

    // 移除格式化金额显示函数

    // 生成头像颜色
    const getAvatarColor = () => {
        const colors = [
            'from-blue-400 to-blue-500',
            'from-orange-400 to-orange-500',
            'from-purple-400 to-purple-500',
            'from-yellow-400 to-yellow-500',
            'from-red-400 to-red-500',
            'from-green-400 to-green-500',
            'from-indigo-400 to-indigo-500',
            'from-pink-400 to-pink-500'
        ];
        // 根据客户编号生成一致的颜色
        const index = clientId ? parseInt(clientId.slice(-1)) || 0 : 0;
        return colors[index % colors.length];
    };

    // 客户信息（使用当前登录用户数据）
    const client = {
        name: currentUser.NAME1,
        avatar: currentUser.NAME1.charAt(0),
        color: getAvatarColor(),
        code: currentUser.KUNNR
    };

    // AI 对话历史
    const [messages, setMessages] = useState<Message[]>([]);

    // SessionID 管理
    const [currentSessionId, setCurrentSessionId] = useState<string>('');

    // 初始化SessionID
    useEffect(() => {
        const sessionId = SessionManager.initSession();
        setCurrentSessionId(sessionId);
        console.log('ChatPage: 初始化会话ID:', sessionId);
    }, []);

    // 检查上次会话
    useEffect(() => {
        const checkLastSession = async () => {
            // 只检查一次，且需要有 weChatOpenId 和 currentSessionId
            if (hasCheckedLastSession || !weChatOpenId || !currentSessionId) {
                console.log('跳过会话检查:', {
                    hasCheckedLastSession,
                    hasWeChatOpenId: !!weChatOpenId,
                    hasCurrentSessionId: !!currentSessionId
                });
                return;
            }

            setHasCheckedLastSession(true);

            try {
                console.log('检查上次会话，weChatOpenId:', weChatOpenId);
                const response = await getLastSession(weChatOpenId);

                if (response.success && response.data && response.data.length > 0) {
                    console.log('找到上次会话:', response.data);
                    // 获取 sessionId（从第一条消息中获取）
                    const sessionId = response.data[0].sessionId;
                    setLastSessionId(sessionId);
                    setLastSessionData(response.data);
                    setHasPendingRestorePrompt(true);
                } else {
                    console.log('没有找到上次会话');
                }
            } catch (error) {
                console.error('检查上次会话失败:', error);
            }
        };

        checkLastSession();
    }, [weChatOpenId, currentSessionId, hasCheckedLastSession]);

    useEffect(() => {
        if (!showEntryChoiceModal && hasPendingRestorePrompt) {
            setShowRestoreSessionModal(true);
            setHasPendingRestorePrompt(false);
        }
    }, [showEntryChoiceModal, hasPendingRestorePrompt]);

    // 恢复上次会话
    const handleRestoreSession = () => {
        console.log('恢复上次会话:', lastSessionId);
        setCurrentSessionId(lastSessionId);

        // 将上次会话的消息转换为 Message 格式
        const restoredMessages: Message[] = lastSessionData.map(msg => ({
            id: msg.id,
            content: msg.content,
            sender: msg.sender === 1 ? 'user' : 'client',
            timestamp: new Date(msg.createdAt).toLocaleTimeString('zh-CN', {
                hour: '2-digit',
                minute: '2-digit'
            }),
        }));

        setMessages(restoredMessages);
        setShowRestoreSessionModal(false);
        showToast('已恢复上次会话', 'success');
    };

    // 不恢复，开始新会话
    const handleStartNewSession = () => {
        console.log('开始新会话');
        setShowRestoreSessionModal(false);
        setHasPendingRestorePrompt(false);
        // 保持当前的新 sessionId，不做任何修改
    };

    // 开始新会话的函数
    const startNewSession = () => {
        const newSessionId = SessionManager.startNewSession();
        setCurrentSessionId(newSessionId);
        setMessages([]); // 清空消息历史
        console.log('ChatPage: 开始新会话:', newSessionId);
    };

    // 初始化语音录制服务
    useEffect(() => {
        voiceRecordingService.setCallbacks({
            onTranscription: (result: TranscriptionResult) => {
                console.log('收到转录结果:', result);
                if (result.text) {
                    if (result.isFinal) {
                        // 最终结果，添加到语音转录文本状态，不添加到输入框
                        console.log('处理最终转录结果:', result.text);

                        // 检查是否已经处理过这个最终结果，避免重复累积
                        const currentTranscript = currentRecordingTranscriptRef.current;
                        if (!currentTranscript.includes(result.text)) {
                            // 累计到当前录音的文本（用于音频消息中的"转文字"）
                            currentRecordingTranscriptRef.current = currentTranscript ?
                                `${currentTranscript} ${result.text}` : result.text;

                            // 更新语音转录文本状态（仅用于语音模态框显示）
                            setVoiceTranscriptText(current => {
                                // 避免重复添加相同的文本
                                if (current.includes(result.text)) {
                                    return current;
                                }
                                return current ? `${current} ${result.text}` : result.text;
                            });
                        }

                        setTranscriptionText(''); // 清空临时转录文本
                        setHasProcessedFinalResult(true); // 标记已处理最终结果
                    } else {
                        // 临时结果，显示在转录文本中（替换，不累加）
                        setTranscriptionText(result.text);
                    }
                }
            },
            onError: (error: Error) => {
                console.error('语音录制错误:', error);
                const errorMessage = error?.message || String(error) || '未知的语音录制错误';
                console.error('语音录制详细错误信息:', errorMessage);
                setVoiceConnectionStatus('error');
                // 显示错误提示
                setPermissionType('microphone');
                setPermissionError(`语音录制错误: ${errorMessage}`);
                setShowPermissionModal(true);
                setShowVoiceModal(false);
            },
            onStatusChange: (status) => {
                console.log('语音录制状态变化:', status);
                setVoiceConnectionStatus(status);
                setIsVoiceRecording(status === 'recording');
            },
            onRecordingComplete: ({ blob, durationMs, mimeType }) => {
                try {
                    const tempId = Date.now();
                    const localUrl = URL.createObjectURL(blob);
                    const transcribed = currentRecordingTranscriptRef.current || '';

                    // 先在聊天中加入临时语音消息
                    const tempAudioMsg: Message = {
                        id: tempId,
                        content: transcribed ? transcribed : '语音消息',
                        sender: 'user',
                        timestamp: getCurrentTime(),
                        metadata: messageService.buildVoiceMetadata({
                            success: true,
                            message: '语音消息',
                            fileName: `voice_${tempId}.wav`,
                            fileKey: 'voice',
                            fileUrl: localUrl,
                            fileSize: 0,
                            contentType: mimeType,
                            uploadTime: Date.now(),
                            error: null
                        }, transcribed, durationMs)
                    };
                    setMessages(prev => [...prev, tempAudioMsg]);

                    // 准备发送给AI的内容，但等上传完成后再发送
                    const transcribedText = transcribed && transcribed.trim() ? transcribed : '';
                    const payloadText = transcribedText || '用户发送了一条语音消息';
                    console.log('准备语音消息，等待上传完成:', payloadText);


                    // 上传音频文件到后端，获取URL后再发送给AI
                    const filename = `voice_${tempId}.wav`;
                    fileUploadService
                        .uploadBlobWithText(blob, filename, transcribed, currentSessionId)
                        .then(async (res) => {
                            if (res.success && res.raw) {
                                // 优先使用文件上传接口返回的fileUrl字段
                                const url = res.raw.fileUrl || res.url || res.raw.url;
                                if (url) {
                                    console.log('语音文件上传成功，获得URL:', url);

                                    // 构建包含真实URL的语音metadata
                                    const voiceMetadata = messageService.buildVoiceMetadata({
                                        success: true,
                                        message: '语音消息',
                                        fileName: filename,
                                        fileKey: 'voice',
                                        fileUrl: url, // 使用真实的上传URL
                                        fileSize: blob.size,
                                        contentType: mimeType,
                                        uploadTime: Date.now(),
                                        error: null
                                    }, transcribed, durationMs);

                                    // 更新消息中的URL
                                    setMessages(prev => prev.map(m =>
                                        m.id === tempId
                                            ? {
                                                ...m,
                                                metadata: {
                                                    ...m.metadata,
                                                    voice_metadata: {
                                                        ...m.metadata?.voice_metadata,
                                                        url: url
                                                    }
                                                }
                                            }
                                            : m
                                    ));

                                    // 现在发送给AI，包含真实的URL
                                    console.log('发送语音消息给AI，包含URL:', url);
                                    sendAiResponseForContent(payloadText, voiceMetadata);
                                } else {
                                    console.warn('语音上传成功但未获得URL');
                                    showToast('语音上传异常：未获得文件URL', 'error');
                                }
                            } else {
                                console.warn('语音上传失败:', res.error);
                                showToast('语音上传失败', 'error');
                            }
                        })
                        .catch((err) => {
                            console.error('语音上传异常:', err);
                            showToast('语音上传异常', 'error');
                        })
                        .finally(() => {
                            // 清理累计文本
                            currentRecordingTranscriptRef.current = '';
                        });
                } catch (e) {
                    console.error('处理音频消息失败:', e);
                    currentRecordingTranscriptRef.current = '';
                }
            }
        });

        // 组件卸载时清理资源
        return () => {
            voiceRecordingService.cleanup();
        };
    }, []);

    // 录音计时器
    useEffect(() => {
        let timer: number | null = null;
        if (isVoiceRecording) {
            setRecordingElapsedSec(0);
            timer = window.setInterval(() => {
                setRecordingElapsedSec(prev => Math.min(prev + 1, 60));
            }, 1000) as unknown as number;
        } else {
            setRecordingElapsedSec(0);
        }
        return () => {
            if (timer !== null) window.clearInterval(timer);
        };
    }, [isVoiceRecording]);

    // 监听语音模态框打开，自动开始录音
    useEffect(() => {
        if (showVoiceModal && !isVoiceRecording && voiceConnectionStatus === 'disconnected') {
            // 延迟一点开始录音，让UI先显示
            const timer = setTimeout(async () => {
                try {
                    console.log('模态框已打开，开始录音流程...');
                    console.log('当前语音连接状态:', voiceConnectionStatus);
                    console.log('是否正在录音:', isVoiceRecording);

                    // 再次检查状态，避免重复启动
                    if (!isVoiceRecording && voiceConnectionStatus === 'disconnected') {
                        await voiceRecordingService.startRecording();
                        console.log('录音启动成功');
                    } else {
                        console.log('录音已在进行中，跳过重复启动');
                    }
                } catch (error) {
                    console.error('自动开始录音失败:', error);
                    const errorMessage = error instanceof Error ? error.message :
                        (typeof error === 'string' ? error :
                            typeof error === 'object' ? JSON.stringify(error) :
                                '录音启动失败');
                    console.error('自动开始录音详细错误:', errorMessage);
                    setPermissionType('microphone');
                    setPermissionError(`录音启动失败: ${errorMessage}`);
                    setShowPermissionModal(true);
                    setShowVoiceModal(false);
                }
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [showVoiceModal, isVoiceRecording, voiceConnectionStatus]);

    // 监听语音模态框关闭，确保清理工作
    useEffect(() => {
        if (!showVoiceModal && (isVoiceRecording || voiceConnectionStatus !== 'disconnected')) {
            console.log('模态框已关闭，执行清理工作...');
            // 确保语音服务被正确停止和清理
            try {
                voiceRecordingService.cleanup();
                setIsVoiceRecording(false);
                setVoiceConnectionStatus('disconnected');
                setTranscriptionText('');
                setVoiceTranscriptText('');
                currentRecordingTranscriptRef.current = '';
            } catch (error) {
                console.warn('清理语音服务时出错:', error);
            }
        }
    }, [showVoiceModal, isVoiceRecording, voiceConnectionStatus]);

    // 头像下拉菜单外部点击关闭
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (avatarDropdownRef.current && !avatarDropdownRef.current.contains(event.target as Node)) {
                setShowAvatarDropdown(false);
            }
        };

        if (showAvatarDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAvatarDropdown]);





    // 打开语音录制模态框
    const openVoiceModal = async () => {
        try {
            console.log('正在打开语音录制模态框...');

            // 简化权限检查：直接打开模态框，让录音流程处理权限
            // 这样可以避免重复的权限检查导致的问题

            // 重置转录状态
            setTranscriptionText('');
            setVoiceTranscriptText('');
            setHasProcessedFinalResult(false);
            currentRecordingTranscriptRef.current = '';

            // 直接打开模态框，权限检查在录音启动时进行
            setShowVoiceModal(true);
            console.log('语音录制模态框已打开');

        } catch (error) {
            console.error('打开语音模态框失败:', error);
            const errorMessage = error instanceof Error ? error.message :
                (typeof error === 'string' ? error :
                    typeof error === 'object' ? JSON.stringify(error) :
                        '语音功能启动失败');
            console.error('打开语音模态框详细错误:', errorMessage);
            setPermissionType('microphone');
            setPermissionError(errorMessage);
            setShowPermissionModal(true);
        }
    };

    // 开始录音
    const startVoiceRecording = async () => {
        try {
            console.log('开始录音，当前状态:', voiceConnectionStatus, '是否正在录音:', isVoiceRecording);

            // 检查是否已经在录音中，避免重复启动
            if (isVoiceRecording || voiceConnectionStatus === 'recording') {
                console.log('录音已在进行中，跳过重复启动');
                return;
            }

            await voiceRecordingService.startRecording();
        } catch (error) {
            console.error('开始录音失败:', error);
            const errorMessage = error instanceof Error ? error.message :
                (typeof error === 'string' ? error :
                    typeof error === 'object' ? JSON.stringify(error) :
                        '录音启动失败');
            console.error('开始录音详细错误:', errorMessage);
            setPermissionType('microphone');
            setPermissionError(errorMessage);
            setShowPermissionModal(true);
            setShowVoiceModal(false);
        }
    };

    const stopVoiceRecording = async () => {
        try {
            console.log('停止录音...');
            await voiceRecordingService.stopRecording();

            // 清理状态
            setTranscriptionText('');
            setVoiceTranscriptText('');
            setHasProcessedFinalResult(false);
            currentRecordingTranscriptRef.current = '';

            // 延迟关闭模态框，让用户看到"录音完成"状态
            setTimeout(() => {
                cleanupVoiceRecording();
            }, 800);
        } catch (error) {
            console.error('停止录音失败:', error);
            cleanupVoiceRecording();
        }
    };

    // 清理语音录制相关状态和服务
    const cleanupVoiceRecording = () => {
        console.log('清理语音录制状态...');

        // 清理状态
        setShowVoiceModal(false);
        setTranscriptionText('');
        setVoiceTranscriptText('');
        setHasProcessedFinalResult(false);
        setIsVoiceRecording(false);
        setVoiceConnectionStatus('disconnected');
        currentRecordingTranscriptRef.current = '';

        // 清理语音录制服务
        try {
            voiceRecordingService.cleanup();
        } catch (error) {
            console.warn('清理语音服务失败:', error);
        }

        // 调整输入框高度
        setTimeout(() => {
            adjustTextareaHeight();
        }, 100);
    };

    const cancelVoiceRecording = async () => {
        try {
            console.log('取消录音...');
            await voiceRecordingService.stopRecording();
            setVoiceTranscriptText(''); // 清空已转录的文本
            cleanupVoiceRecording();
        } catch (error) {
            console.error('取消录音失败:', error);
            cleanupVoiceRecording();
        }
    };

    // 触发系统相机（文件选择）
    const triggerSystemCamera = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // 触发附件选择
    const triggerAttachmentUpload = () => {
        if (attachmentInputRef.current) {
            attachmentInputRef.current.click();
        }
    };

    // 选择文件后上传
    const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
        if (selectedFiles.length === 0) return;

        try {
            if (selectedFiles.length > 1) {
                await handleAttachmentUpload(selectedFiles);
                return;
            }

            const file = selectedFiles[0];
            // 判断文件类型
            const isImage = imageUploadService.isValidImageType(file);

            if (isImage) {
                // 处理图片文件
                if (!imageUploadService.isValidImageSize(file, 20)) {
                    showToast('图片过大，最大20MB', 'error');
                    return;
                }
            } else {
                // 处理其他类型文件作为附件
                const maxSize = 50 * 1024 * 1024; // 50MB
                if (file.size > maxSize) {
                    showToast('文件过大，最大50MB', 'error');
                    return;
                }

                // 调用附件上传处理
                await handleAttachmentUpload([file]);
                return;
            }

            // 先生成本地预览
            const localPreview = URL.createObjectURL(file);
            const tempMessage: Message = {
                id: Date.now(),
                content: '正在上传图片...',
                sender: 'user',
                timestamp: getCurrentTime(),
            };
            setMessages(prev => [...prev, tempMessage]);

            // 可选：前端压缩后再传，提高速度
            // 如果需要压缩，取消下面注释：
            // const compressedBase64 = await imageUploadService.compressImage(file, 0.8, 1920, 1080);
            // const uploadResult = await imageUploadService.uploadBase64Image(compressedBase64, file.name, currentSessionId);

            // 默认：直接 multipart 上传
            const uploadResult = await imageUploadService.uploadFile(file, currentSessionId);

            if (uploadResult.success) {
                const imageUrl = uploadResult.imageUrl || localPreview;

                // 构建图片消息的metadata
                const uploadResponse: FileUploadResponse = {
                    success: true,
                    message: '文件上传成功',
                    fileName: file.name,
                    fileKey: `uploads/images/${file.name}`,
                    fileUrl: imageUrl || '',
                    fileSize: file.size,
                    contentType: file.type,
                    uploadTime: Date.now(),
                    error: null
                };

                const metadata = messageService.buildImageMetadata(uploadResponse, '[图片]');

                setMessages(prev => prev.map(msg =>
                    msg.id === tempMessage.id
                        ? {
                            ...msg,
                            content: '[图片]',
                            metadata: metadata
                        }
                        : msg
                ));

                // 按照 OpenAI vision 格式发送图片消息
                try {
                    sendAiResponseForImageContent(imageUrl, metadata);
                } catch (error) {
                    console.error('处理图片消息失败:', error);
                }

                showToast('照片上传成功', 'success');
            } else {
                setMessages(prev => prev.map(msg =>
                    msg.id === tempMessage.id
                        ? { ...msg, content: `[图片上传失败] ${uploadResult.error || ''}` }
                        : msg
                ));
                showToast('图片上传失败', 'error');
            }
        } catch (err) {
            setMessages(prev => prev.map(msg =>
                msg.content.startsWith('正在上传图片')
                    ? { ...msg, content: `[图片处理失败] ${err instanceof Error ? err.message : '未知错误'}` }
                    : msg
            ));
            showToast('图片处理失败', 'error');
        } finally {
            // 清空 input，避免同一文件无法再次选择
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // 处理附件选择
    const handleAttachmentSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        if (files.length === 0) return;

        try {
            // 检查文件大小
            const maxSize = 50 * 1024 * 1024; // 50MB
            const oversized = files.find(file => file.size > maxSize);
            if (oversized) {
                showToast(`文件过大：${oversized.name} 超过50MB`, 'error');
                return;
            }

            // 调用附件上传处理
            await handleAttachmentUpload(files);
        } catch (err) {
            console.error('附件选择异常:', err);
            showToast('附件选择异常', 'error');
        } finally {
            // 清空 input，避免同一文件无法再次选择
            if (attachmentInputRef.current) attachmentInputRef.current.value = '';
        }
    };

    const handleChooseRecommendation = () => {
        setShowEntryChoiceModal(false);
        if (hasPendingRestorePrompt || lastSessionData.length > 0) {
            setShowRestoreSessionModal(true);
            setHasPendingRestorePrompt(false);
        }
    };

    const handleChooseUpload = () => {
        setShowEntryChoiceModal(false);
        setHasPendingRestorePrompt(false);
        navigate('/upload-materials');
    };

    // 拍照处理函数（直接触发系统相机并走后端直传）
    const handleCameraCapture = async () => {
        try {
            setIsCameraLoading(true);
            triggerSystemCamera();
        } finally {
            setIsCameraLoading(false);
        }
    };

    // 附件上传处理函数
    const handleAttachmentUpload = async (files: File[]) => {
        if (!files.length) return;

        const maxSize = 50 * 1024 * 1024; // 50MB
        const oversized = files.find(file => file.size > maxSize);
        if (oversized) {
            showToast(`文件过大：${oversized.name} 超过50MB`, 'error');
            return;
        }

        const timestamp = Date.now();
        const tempEntries = files.map((file, index) => ({
            tempId: timestamp + index,
            file
        }));

        const tempMessages: Message[] = tempEntries.map(({ tempId, file }) => ({
            id: tempId,
            content: `正在上传附件：${file.name}`,
            sender: 'user',
            timestamp: getCurrentTime(),
        }));

        setMessages(prev => [...prev, ...tempMessages]);

        const matchEntry = (() => {
            const matched = new Set<number>();
            return (item?: FileUploadResponseItem) => {
                const candidates = [
                    item?.fileName,
                    item?.originalName,
                    item?.name,
                    item?.fileOriginalName,
                    item?.sourceFileName
                ].filter((name): name is string => !!name);

                for (const candidate of candidates) {
                    const lower = candidate.toLowerCase();
                    const found = tempEntries.find(entry =>
                        entry.file.name.toLowerCase() === lower && !matched.has(entry.tempId)
                    );
                    if (found) {
                        matched.add(found.tempId);
                        return found;
                    }
                }

                const fallback = tempEntries.find(entry => !matched.has(entry.tempId));
                if (fallback) {
                    matched.add(fallback.tempId);
                    return fallback;
                }
                return undefined;
            };
        })();

        try {
            const uploadResult = await fileUploadService.uploadFiles(files, { sessionId: currentSessionId });
            const successfulUploads: { entry: typeof tempEntries[number]; normalized: FileUploadResponse }[] = [];
            const successEntries = uploadResult.uploaded
                .map(item => {
                    const entry = matchEntry(item);
                    return entry ? { entry, item } : null;
                })
                .filter((value): value is { entry: typeof tempEntries[number]; item: FileUploadResponseItem } => !!value);

            const failedEntries = uploadResult.failed
                .map(item => {
                    const entry = matchEntry(item);
                    return entry ? { entry, item } : null;
                })
                .filter((value): value is { entry: typeof tempEntries[number]; item: FileUploadResponseItem } => !!value);

            // 将剩余未匹配的项标记为失败
            const unmatched = tempEntries.filter(
                entry => !successEntries.some(success => success.entry.tempId === entry.tempId) &&
                    !failedEntries.some(failure => failure.entry.tempId === entry.tempId)
            );
            unmatched.forEach(entry => {
                failedEntries.push({
                    entry,
                    item: {
                        fileName: entry.file.name,
                        error: uploadResult.message || '上传失败'
                    }
                });
            });

            const updates = new Map<number, Partial<Message>>();

            successEntries.forEach(({ entry, item }) => {
                const normalized: FileUploadResponse = {
                    success: true,
                    message: item?.message || '文件上传成功',
                    fileName: item?.fileName || entry.file.name,
                    fileKey: item?.fileKey || item?.fileId || item?.fileName || entry.file.name,
                    fileUrl: item?.fileUrl || item?.url || item?.path || '',
                    fileSize: item?.fileSize || entry.file.size,
                    contentType: item?.contentType || entry.file.type || 'application/octet-stream',
                    uploadTime: item?.uploadTime || Date.now(),
                    error: null
                };

                updates.set(entry.tempId, {
                    content: `[附件] ${normalized.fileName || entry.file.name}`
                });
                successfulUploads.push({ entry, normalized });
            });

            failedEntries.forEach(({ entry, item }) => {
                const reason = item?.message || item?.error || uploadResult.message || '上传失败';
                updates.set(entry.tempId, {
                    content: `[附件上传失败] ${entry.file.name}${reason ? ` - ${reason}` : ''}`
                });
            });

            if (updates.size > 0) {
                setMessages(prev => prev.map(msg => {
                    if (!updates.has(msg.id)) return msg;
                    return { ...msg, ...updates.get(msg.id)! };
                }));
            }

            if (!uploadResult.success && uploadResult.failedCount === uploadResult.total && uploadResult.total > 0) {
                showToast(uploadResult.message || '附件上传失败', 'error');
            } else if (uploadResult.failedCount > 0) {
                const infoMessage = uploadResult.message ||
                    `部分成功：成功 ${uploadResult.succeeded} 个，失败 ${uploadResult.failedCount} 个`;
                showToast(infoMessage, 'info');
            } else {
                showToast(uploadResult.message || '附件上传成功', 'success');
            }

            if (successfulUploads.length > 0) {
                try {
                    if (successfulUploads.length === 1) {
                        const { normalized, entry } = successfulUploads[0];
                        const fileName = normalized.fileName || entry.file.name || '未命名附件';
                        const metadata = messageService.buildAttachmentMetadata(normalized, `附件：${fileName}`);
                        const payloadUrl = normalized.fileUrl || '（无）';
                        const payloadText = `用户上传了附件：${fileName}，文件地址：${payloadUrl}`;
                        await sendAiResponseForContent(payloadText, metadata);
                    } else {
                        const payloadLines = successfulUploads.map(({ normalized, entry }, index) => {
                            const fileName = normalized.fileName || entry.file.name || '未命名附件';
                            return `${index + 1}. ${fileName} - ${normalized.fileUrl || '（无URL）'}`;
                        });
                        const attachments = successfulUploads.map(({ normalized, entry }) => {
                            const fileName = normalized.fileName || entry.file.name || '未命名附件';
                            return {
                                file_id: normalized.fileKey || normalized.fileName || fileName,
                                filename: fileName,
                                url: normalized.fileUrl,
                                mime_type: normalized.contentType,
                                size: normalized.fileSize,
                                description: `附件：${fileName}`
                            };
                        });
                        const batchMetadata = {
                            message_type: 'attachment_batch',
                            attachment_batch_metadata: {
                                count: successfulUploads.length,
                                attachments
                            }
                        };
                        const payloadText = `用户上传了${successfulUploads.length}个附件：\n${payloadLines.join('\n')}`;
                        await sendAiResponseForContent(payloadText, batchMetadata);
                    }
                } catch (error) {
                    console.error('处理附件消息失败:', error);
                }
            }
        } catch (err) {
            console.error('附件上传异常:', err);
            const errorMessage = err instanceof Error ? err.message : '附件上传异常';
            setMessages(prev => prev.map(msg =>
                tempEntries.some(entry => entry.tempId === msg.id)
                    ? { ...msg, content: `[附件上传失败] ${msg.content.replace('正在上传附件：', '')} - ${errorMessage}` }
                    : msg
            ));
            showToast(errorMessage, 'error');
        }
    };

    // 移除位置分享功能

    // 自动滚动到底部
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // 获取当前时间字符串
    const getCurrentTime = () => {
        const now = new Date();
        return now.toLocaleTimeString('zh-CN', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };



    // 创建AI消息的通用函数
    const createAIMessage = (): Message => {
        return {
            id: Date.now() + 1,
            content: '',
            sender: 'client',
            timestamp: getCurrentTime(),
            isStreaming: true,
        };
    };

    // 构建聊天历史的通用函数
    const buildChatHistory = (userContent: string): ChatMessage[] => {
        // const systemMessage: ChatMessage = {
        //     role: 'system',
        //     content: client
        //         ? `你是${client.name}的客户代表，正在与销售人员进行商务对话。请以客户的身份回复，表现出对产品或服务的兴趣，并提出相关的问题或需求。保持专业和友好的语调。`
        //         : '你是客户代表助手，正在与销售人员进行对话。请以客户视角回复，保持专业和友好。'
        // };

        const messageHistory = messages.map(msg => {
            const chatMsg: ChatMessage = {
                role: msg.sender === 'user' ? 'user' as const : 'assistant' as const,
                content: msg.content,
            };

            // 如果消息有 metadata，直接使用
            if (msg.metadata) {
                chatMsg.metadata = msg.metadata;
            }

            return chatMsg;
        });

        return [

            ...messageHistory,
            {
                role: 'user' as const,
                content: userContent,
            }
        ];
    };

    // 构建带metadata的聊天历史
    const buildChatHistoryWithMetadata = (userContent: string, metadata: any): ChatMessage[] => {
        // const systemMessage: ChatMessage = {
        //     role: 'system',
        //     content: client
        //         ? `你是${client.name}的客户代表，正在与销售人员进行商务对话。请以客户的身份回复，表现出对产品或服务的兴趣，并提出相关的问题或需求。保持专业和友好的语调。`
        //         : '你是客户代表助手，正在与销售人员进行对话。请以客户视角回复，保持专业和友好。'
        // };

        const messageHistory = messages.map(msg => {
            const chatMsg: ChatMessage = {
                role: msg.sender === 'user' ? 'user' as const : 'assistant' as const,
                content: msg.content,
            };

            // 如果消息有 metadata，直接使用
            if (msg.metadata) {
                chatMsg.metadata = msg.metadata;
            }

            return chatMsg;
        });

        return [

            ...messageHistory,
            {
                role: 'user' as const,
                content: userContent,
                metadata: metadata // 将metadata添加到最后一条用户消息中
            }
        ];
    };

    // 构建带图片内容的聊天历史（OpenAI vision格式）
    const buildChatHistoryWithImageContent = (imageUrl: string, metadata: any): ChatMessage[] => {
        // const systemMessage: ChatMessage = {
        //     role: 'system',
        //     content: client
        //         ? `你是${client.name}的客户代表，正在与销售人员进行商务对话。请以客户的身份回复，表现出对产品或服务的兴趣，并提出相关的问题或需求。保持专业和友好的语调。`
        //         : '你是客户代表助手，正在与销售人员进行对话。请以客户视角回复，保持专业和友好。'
        // };

        const messageHistory = messages.map(msg => {
            const chatMsg: ChatMessage = {
                role: msg.sender === 'user' ? 'user' as const : 'assistant' as const,
                content: msg.content,
            };

            // 如果消息有 metadata，直接使用
            if (msg.metadata) {
                chatMsg.metadata = msg.metadata;
            }

            return chatMsg;
        });

        // 按照 OpenAI vision 格式构建图片消息
        const imageMessage: ChatMessage = {
            role: 'user' as const,
            content: [
                {
                    type: 'text',
                    text: '帮我描述理解图片的内容'
                },
                {
                    type: 'image_url',
                    image_url: {
                        url: imageUrl,
                        detail: 'auto'
                    }
                }
            ],
            metadata: metadata
        };

        return [
            // systemMessage,
            ...messageHistory,
            imageMessage
        ];
    };







    const handleSendMessage = async () => {
        if (newMessage.trim() === '' || isLoading) {
            return;
        }

        const userMessage: Message = {
            id: Date.now(),
            content: newMessage.trim(),
            sender: 'user',
            timestamp: getCurrentTime(),
        };

        // 添加用户消息
        setMessages(prev => [...prev, userMessage]);
        setNewMessage('');
        setIsLoading(true);

        // 文本消息不需要调用saveMessageToBackend，直接通过AI接口处理
        // 所有消息（包括文本）都通过 /api/v1/chat/completions 接口处理

        // 创建AI响应消息
        const aiMessage = createAIMessage();
        const aiMessageId = aiMessage.id;
        setMessages(prev => [...prev, aiMessage]);
        console.log('Added empty AI message:', aiMessage);

        try {
            // 准备聊天历史
            const chatHistory = buildChatHistory(userMessage.content);

            try {
                console.log('发送AI请求，用户ID:', userInfo?.userId || client.code);

                // 流式获取AI响应
                const stream = aiChatService.streamChatCompletion(chatHistory, {
                    temperature: 0.1,
                    max_tokens: 4000,
                    customerId: userInfo?.userId || client.code, // 传入当前用户ID
                    sessionId: currentSessionId, // 传入当前会话ID
                    onError: (error) => {
                        console.error('AI Chat Error:', error);
                        setMessages(prev => prev.map(msg =>
                            msg.id === aiMessageId
                                ? { ...msg, content: '抱歉，发生了错误，请稍后重试。', isStreaming: false }
                                : msg
                        ));
                    }
                });

                let accumulatedContent = '';
                let chunkCount = 0;

                console.log('🌊 开始处理流式响应...');

                for await (const chunk of stream) {
                    if (chunk) {
                        chunkCount++;
                        accumulatedContent += chunk;

                        console.log(`📝 ChatPage收到第${chunkCount}个内容块:`, chunk.substring(0, 50) + (chunk.length > 50 ? '...' : ''));

                        // 更新AI消息内容
                        setMessages(prev => prev.map(msg =>
                            msg.id === aiMessageId
                                ? { ...msg, content: accumulatedContent, isStreaming: true }
                                : msg
                        ));
                    }
                }

                console.log('✅ ChatPage流式响应处理完成，总共收到', chunkCount, '个内容块，总长度:', accumulatedContent.length);
            } catch (streamError) {
                console.error('Stream error:', streamError);
                const errorMessage = streamError instanceof Error ? streamError.message : String(streamError);
                setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                        ? { ...msg, content: '流式传输出错：' + errorMessage, isStreaming: false }
                        : msg
                ));
            }

            // 标记流式传输完成
            setMessages(prev => prev.map(msg =>
                msg.id === aiMessageId
                    ? { ...msg, isStreaming: false }
                    : msg
            ));

        } catch (error) {
            console.error('Error sending message:', error);
            setMessages(prev => prev.map(msg =>
                msg.id === aiMessageId
                    ? { ...msg, content: '抱歉，发生了错误，请稍后重试。', isStreaming: false }
                    : msg
            ));
        } finally {
            setIsLoading(false);
        }
    };

    // 发送图片消息给AI（按照OpenAI vision格式）
    const sendAiResponseForImageContent = async (imageUrl: string, messageMetadata?: any) => {
        const aiMessage = createAIMessage();
        const aiMessageId = aiMessage.id;
        setMessages(prev => [...prev, aiMessage]);

        try {
            // 构建带图片的聊天历史
            const chatHistory = buildChatHistoryWithImageContent(imageUrl, messageMetadata);

            console.log('发送图片消息到AI，图片URL:', imageUrl);
            console.log('用户ID:', userInfo?.userId || client.code);

            const stream = aiChatService.streamChatCompletion(chatHistory, {
                temperature: 0.1,
                max_tokens: 4000,
                customerId: userInfo?.userId || client.code,
                sessionId: currentSessionId, // 传入当前会话ID
                onError: (error) => {
                    console.error('AI Chat Error:', error);
                    setMessages(prev => prev.map(msg =>
                        msg.id === aiMessageId
                            ? { ...msg, content: '抱歉，发生了错误，请稍后重试。', isStreaming: false }
                            : msg
                    ));
                }
            });

            let accumulatedContent = '';
            for await (const chunk of stream) {
                if (!chunk) continue;
                accumulatedContent += chunk;
                setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                        ? { ...msg, content: accumulatedContent, isStreaming: true }
                        : msg
                ));
            }

            setMessages(prev => prev.map(msg =>
                msg.id === aiMessageId
                    ? { ...msg, isStreaming: false }
                    : msg
            ));
        } catch (error) {
            console.error('Error sending image to AI:', error);
            setMessages(prev => prev.map(msg =>
                msg.id === aiMessageId
                    ? { ...msg, content: '抱歉，发生了错误，请稍后重试。', isStreaming: false }
                    : msg
            ));
        } finally {
            setIsLoading(false);
        }
    };

    // 直接以给定内容请求AI回复（不重复插入一条新的用户文本消息）
    const sendAiResponseForContent = async (userContent: string, messageMetadata?: any) => {
        const aiMessage = createAIMessage();
        const aiMessageId = aiMessage.id;
        setMessages(prev => [...prev, aiMessage]);

        try {
            // 如果有metadata，构建带metadata的聊天历史
            const chatHistory = messageMetadata
                ? buildChatHistoryWithMetadata(userContent, messageMetadata)
                : buildChatHistory(userContent);

            console.log('发送AI请求到 /api/v1/chat/completions，最后一条用户内容：', userContent);
            console.log('用户ID:', userInfo?.userId || client.code);

            const stream = aiChatService.streamChatCompletion(chatHistory, {
                temperature: 0.1,
                max_tokens: 4000,
                customerId: userInfo?.userId || client.code, // 传入当前用户ID
                sessionId: currentSessionId, // 传入当前会话ID
                onError: (error) => {
                    console.error('AI Chat Error:', error);
                    setMessages(prev => prev.map(msg =>
                        msg.id === aiMessageId
                            ? { ...msg, content: '抱歉，发生了错误，请稍后重试。', isStreaming: false }
                            : msg
                    ));
                }
            });

            let accumulatedContent = '';
            for await (const chunk of stream) {
                if (!chunk) continue;
                accumulatedContent += chunk;
                setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                        ? { ...msg, content: accumulatedContent, isStreaming: true }
                        : msg
                ));
            }

            setMessages(prev => prev.map(msg =>
                msg.id === aiMessageId
                    ? { ...msg, isStreaming: false }
                    : msg
            ));
        } catch (error) {
            console.error('Error sending AI response for content:', error);
            setMessages(prev => prev.map(msg =>
                msg.id === aiMessageId
                    ? { ...msg, content: '抱歉，发生了错误，请稍后重试。', isStreaming: false }
                    : msg
            ));
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // 自动调整输入框高度
    const adjustTextareaHeight = () => {
        if (textareaRef.current) {
            const textarea = textareaRef.current;
            const container = textarea.closest('.input-container') as HTMLElement;

            textarea.style.height = 'auto';
            const scrollHeight = textarea.scrollHeight;
            const maxHeight = 160; // 最大高度
            const minHeight = 45; // 最小高度

            if (scrollHeight > maxHeight) {
                textarea.style.height = `${maxHeight}px`;
                textarea.style.overflowY = 'auto';
                if (container) container.style.height = `${maxHeight + 28}px`; // 减少padding
            } else if (scrollHeight < minHeight) {
                textarea.style.height = `${minHeight}px`;
                textarea.style.overflowY = 'hidden';
                if (container) container.style.height = `${minHeight + 28}px`;
            } else {
                textarea.style.height = `${scrollHeight}px`;
                textarea.style.overflowY = 'hidden';
                if (container) container.style.height = `${scrollHeight + 28}px`;
            }
        }
    };

    // 监听输入变化
    React.useEffect(() => {
        adjustTextareaHeight();
    }, [newMessage]);

    // 移除加载状态和客户验证，直接进入聊天界面

    return (
        <div className="h-screen chat-bg-gradient overflow-hidden">
            <div className="w-full max-w-4xl mx-auto h-full flex flex-col chat-container md:max-w-2xl lg:max-w-4xl xl:max-w-5xl">
                {/* Header - 简约设计 */}
                <SafeAreaWrapper top={true} className="flex-shrink-0 chat-header">
                    <div className="flex items-center justify-between px-6 py-4">
                        {/* 左侧折叠图标 */}
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer">
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </div>

                        {/* 中间新会话按钮 */}
                        <button
                            onClick={startNewSession}
                            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                            title="开始新会话"
                        >
                            新会话
                        </button>

                        {/* 右侧用户头像 */}
                        <div className="flex items-center relative" ref={avatarDropdownRef}>
                            {userInfo && (
                                <button
                                    onClick={() => setShowAvatarDropdown(!showAvatarDropdown)}
                                    className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center hover:bg-gray-500 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
                                    title="用户菜单"
                                >
                                    <span className="text-white text-sm font-medium">
                                        {userInfo.name?.charAt(0) || 'U'}
                                    </span>
                                </button>
                            )}
                            
                            {/* 下拉菜单 */}
                            {showAvatarDropdown && (
                                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                                    <div className="px-4 py-2 border-b border-gray-100">
                                        <p className="text-sm font-medium text-gray-900">
                                            {userInfo?.name || '用户'}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {userInfo?.userId || userInfo?.login || ''}
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center"
                                    >
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                        </svg>
                                        注销
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </SafeAreaWrapper>




                {/* 移除客户统计信息区域 */}

                {/* Messages - 占据剩余所有空间，固定高度内滚动 */}
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                    {messages.length === 0 ? (
                        /* Initial Welcome Screen */
                        <div className="h-full flex flex-col items-center justify-center px-6 welcome-screen">
                            <div className="text-center mb-8">
                                <div className="text-left mb-4">
                                    <h1 className="text-xl font-semibold mb-3 shiny-text">尊敬的合作伙伴您好，</h1>
                                    <div className="text-sm text-gray-700 leading-relaxed">
                                        <p className="mb-3">若您有意向与我们建立合作关系，请根据以下指引提供详细资料，我们将优先审核并安排技术/采购对接。</p>

                                        <div className="space-y-2">
                                            <p><strong>1. 贵司基本信息：</strong>主营业务范围（如PP/PA/PC等）、年产能规模（吨/年）、产线数量</p>

                                            <p><strong>2. 产品资料：</strong>请发送TDS、MSDS、产品资料等文件，提供核心性能参数：密度、拉伸强度/模量、冲击强度、热变形温度、熔融指数等</p>

                                            <p><strong>3. 供应能力：</strong>最小起订量（MOQ）、标准交货周期（从订单确认到交付）</p>

                                            <p><strong>4. 应用领域：</strong>主要应用行业/领域（如汽车内外饰/结构件、家电外壳/连接器、电子电器绝缘件、医疗器械等）</p>

                                            <p><strong>5. 具体应用案例：</strong>请提供具体案例（客户行业、具体产品、性能反馈）<br />
                                                <span className="text-xs text-gray-500 italic">示例：为某头部家电企业提供阻燃PA66用于洗衣机控制面板，已合作5年，年采购量约2000吨，客户反馈耐高温老化性能优于竞品</span>
                                            </p>
                                        </div>

                                        {/* <p className="text-blue-600 font-medium mt-4 text-center">您的任何分享都将对我们非常有价值。我们将对您的信息进行专业评估，期待后续合作。</p> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Chat Messages - 从顶部开始显示 */
                        <div className="px-4 md:px-6 lg:px-8 pt-4 pb-4 space-y-4 md:space-y-6">
                            {messages.map((message) => (
                                <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    {message.sender === 'client' && (
                                        <div className="mr-2 md:mr-3 flex-shrink-0 mt-1">
                                            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center shadow-lg">
                                                <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                </svg>
                                            </div>
                                        </div>
                                    )}
                                    <div className={`max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl px-4 py-3 rounded-2xl message-bubble ${message.sender === 'user'
                                        ? 'message-bubble-user'
                                        : 'message-bubble-ai'
                                        }`}>
                                        {message.sender === 'client' ? (
                                            <div className="relative">
                                                <ThinkingMarkdownRenderer
                                                    content={message.content}
                                                    metadata={message.metadata}
                                                    className="text-sm md:text-base"
                                                />
                                                {message.isStreaming && (
                                                    <div className="inline-flex items-center ml-2">
                                                        <span className="message-loading-dot"></span>
                                                        <span className="message-loading-dot ml-1"></span>
                                                        <span className="message-loading-dot ml-1"></span>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="relative">
                                                <ThinkingMarkdownRenderer
                                                    content={message.content}
                                                    metadata={message.metadata}
                                                    className="text-sm md:text-base"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </div>

                {/* 输入区域 - 固定在底部 */}
                <SafeAreaWrapper bottom={true} className="flex-shrink-0 input-area p-4 relative">


                    {/* 现代化输入框容器 */}
                    <div className="input-container relative">
                        {/* 输入框 */}
                        <div className="w-full">
                            <textarea
                                ref={textareaRef}
                                value={newMessage}
                                onChange={(e) => {
                                    setNewMessage(e.target.value);
                                    // 延迟调整高度，确保状态更新后再调整
                                    setTimeout(adjustTextareaHeight, 0);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                placeholder="Ask anything"
                                className="w-full px-4 pt-3 pb-10 bg-transparent border-none focus:outline-none resize-none text-base placeholder-gray-500 leading-relaxed"
                                style={{
                                    height: '45px',
                                    overflowY: 'hidden'
                                }}
                                disabled={isLoading}
                            />
                        </div>

                        {/* 底部按钮区域 */}
                        <div className="absolute bottom-3 left-0 right-0 flex items-center justify-between px-4">
                            {/* 左下角操作按钮 */}
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="w-8 h-8 rounded-full bg-transparent hover:bg-gray-200 flex items-center justify-center transition-colors"
                                    title="上传文件"
                                >
                                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setShowVoiceModal(true)}
                                    className="w-8 h-8 rounded-full bg-transparent hover:bg-gray-200 flex items-center justify-center transition-colors"
                                    title="语音输入"
                                >
                                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                    </svg>
                                </button>


                            </div>

                            {/* 右下角发送按钮 */}
                            <button
                                onClick={handleSendMessage}
                                disabled={isLoading || newMessage.trim() === ''}
                                className="send-button disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>


                </SafeAreaWrapper>
            </div>

            {/* 快捷操作抽屉 */}
            <QuickActionsDrawer
                isOpen={showQuickActions}
                onClose={() => setShowQuickActions(false)}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onQuickMessage={setNewMessage}
                onVoiceRecord={openVoiceModal}
                onCameraCapture={handleCameraCapture}
                onFileUpload={() => {
                    triggerAttachmentUpload();
                }}
                onScheduleMeeting={() => {
                    console.log('安排会议功能待实现');
                }}
                isVoiceRecording={isVoiceRecording}
                voiceConnectionStatus={voiceConnectionStatus}
                isCameraLoading={isCameraLoading}
            />

            {/* 入口选择弹窗 */}
            {showEntryChoiceModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 md:p-8 space-y-6 animate-fade-in">
                        <div className="space-y-2 text-center">
                            <h3 className="text-2xl font-semibold text-gray-900">欢迎回来</h3>
                            <p className="text-gray-600 text-sm md:text-base">
                                请选择接下来的操作，我们将为你快速安排
                            </p>
                        </div>
                        <div className="grid gap-4">
                            <button
                                onClick={handleChooseRecommendation}
                                className="group rounded-xl border border-transparent bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-5 py-4 text-left transition-all hover:shadow-lg"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-lg font-medium">获取推荐材料</h4>
                                        <p className="mt-1 text-sm text-white/80">
                                            直接进入对话，获取个性化推荐内容
                                        </p>
                                    </div>
                                    <span className="text-white text-2xl group-hover:translate-x-1 transition-transform">
                                        →
                                    </span>
                                </div>
                            </button>
                            <button
                                onClick={handleChooseUpload}
                                className="rounded-xl border border-dashed border-gray-300 bg-white px-5 py-4 text-left transition-all hover:border-blue-400 hover:shadow-md"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-lg font-medium text-gray-900">上传资料</h4>
                                        <p className="mt-1 text-sm text-gray-600">
                                            前往资料上传中心，集中管理文件
                                        </p>
                                    </div>
                                    <span className="text-gray-400 text-2xl">
                                        ⬆
                                    </span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 恢复会话弹窗 */}
            {showRestoreSessionModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-fade-in">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                发现上次会话
                            </h3>
                            <p className="text-gray-600 text-sm">
                                检测到您有 {lastSessionData.length} 条未完成的对话记录
                            </p>
                            <p className="text-gray-500 text-xs mt-2">
                                是否继续上次的对话？
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleStartNewSession}
                                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                            >
                                开始新对话
                            </button>
                            <button
                                onClick={handleRestoreSession}
                                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                            >
                                继续上次对话
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 权限请求模态框 */}
            <PermissionModal
                isOpen={showPermissionModal}
                onClose={() => setShowPermissionModal(false)}
                onRetry={() => {
                    if (permissionType === 'microphone') {
                        setShowVoiceModal(true);
                    }
                }}
                permissionType={permissionType}
                errorMessage={permissionError}
            />

            {/* 语音录制模态框 */}
            <VoiceRecordingModal
                isOpen={showVoiceModal}
                onStartRecording={startVoiceRecording}
                onStopRecording={stopVoiceRecording}
                onCancelRecording={cancelVoiceRecording}
                transcriptionText={transcriptionText}
                newMessage={voiceTranscriptText}
                isVoiceRecording={isVoiceRecording}
                voiceConnectionStatus={voiceConnectionStatus}
                recordingElapsedSec={recordingElapsedSec}
            />

            {/* 隐藏的文件输入 */}
            <input
                ref={fileInputRef}
                type="file"
                accept="*/*"
                multiple
                onChange={handleFileSelected}
                className="hidden"
            />

            {/* 隐藏的附件输入 */}
            <input
                ref={attachmentInputRef}
                type="file"
                accept="*/*"
                multiple
                onChange={handleAttachmentSelected}
                className="hidden"
            />
        </div>
    );
};

export default ChatPage;
